import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, DataTable, FilterChips, FormGrid, OlhoBotao, Pagination, SelectField, TextField } from "@atlas/ui/web";
import type { StatusNaFolha } from "@atlas/types";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { GRADE_FILTROS } from "../../lib/filtro-cadastros";
import { useFiltros } from "../../lib/use-filtros";
import { atlas, apiBaseUrl } from "../../lib/sdk";
import { QUERY_CREDORES_OPERAVEIS, listarCredoresOperaveis, rotuloCredor } from "../../lib/credores-operaveis";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis, rotuloUnidade } from "../../lib/unidades-operaveis";
import type { AdminBateCarteiraResultado } from "@atlas/sdk";
import { ROTULO_STATUS_NA_FOLHA, STATUS_NA_FOLHA } from "../../lib/bate-carteira";
import { AtualidadeDoRelatorio, CardsPorStatus, ForaDaCarteira, colunasDaCarteira } from "../../components/BateCarteiraPaineis";

export function AdminBateCarteira() {
  const bancos = useQuery({ queryKey: QUERY_CREDORES_OPERAVEIS, queryFn: listarCredoresOperaveis });
  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const [bancoId, setBancoId] = useState<number | null>(null);
  const [unidadeId, setPrefeituraId] = useState<number | "all">("all");
  const [competencia, setCompetencia] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  // `total` (do recorte) acompanha o resultado desde a 007 — ver a nota do
  // `recorte` abaixo sobre por que ele e distinto de `totalLinhas`.
  const [resultado, setResultado] = useState<(AdminBateCarteiraResultado & { total?: number }) | null>(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // Status na folha (#347): o que exige acao e' o que nao descontou inteiro, e
  // o filtro isola cada caso. Os cards continuam contando o relatorio inteiro.
  const [status, setStatus] = useState<StatusNaFolha | null>(null);
  const filtros = useFiltros([{ nome: "status", ligado: status !== null, limpar: () => setStatus(null) }], setPagina);
  // Um olho para a tabela inteira: CPF mascarado por padrao, completo ao abrir.
  const [cpfsRevelados, setCpfsRevelados] = useState(false);
  // O `recorteDoServidor` da ao `Pagination` a MESMA forma que o recorte no
  // cliente dava — e o que deixa a diferenca invisivel no controle.
  //
  // `total` e o do RECORTE; `totalLinhas` (nos cards) continua sendo o do
  // relatorio inteiro. Os dois numeros sao diferentes de proposito quando o
  // filtro de status esta ligado.
  const recorte = recorteDoServidor(
    resultado?.linhas ?? [],
    resultado?.total ?? resultado?.totalLinhas ?? 0,
    pagina,
    porPagina,
  );

  // O relatorio e recalculado a cada virada de pagina, e nao ha atalho: ele
  // nao existe em tabela — e conciliacao computada sobre contratos e resultados
  // de desconto. Guardar a lista inteira no cliente para fatiar era justamente
  // o que a 007 veio tirar.
  const gerar = useMutation({
    mutationFn: (p: { pagina: number; porPagina: number; status: StatusNaFolha | null }) =>
      atlas.admin.bateCarteira({
        bancoId: bancoId ?? 0,
        competencia,
        unidadeId: unidadeId === "all" ? undefined : unidadeId,
        format: "json",
        status: p.status ?? undefined,
        limit: p.porPagina,
        offset: (p.pagina - 1) * p.porPagina,
      }),
    onSuccess: (r) => setResultado(r),
    onError: (err) => alert(err instanceof Error ? err.message : "Erro ao gerar"),
  });
  /** Virar pagina refaz o pedido — o relatorio nao fica em memoria. */
  const irPara = (p: number) => { setPagina(p); gerar.mutate({ pagina: p, porPagina, status }); };
  const trocarPorPagina = (n: number) => { setPorPagina(n); setPagina(1); gerar.mutate({ pagina: 1, porPagina: n, status }); };
  /** Trocar o filtro volta pra primeira pagina e refaz o pedido. */
  const trocarStatus = filtros.filtrar((s: StatusNaFolha | null) => {
    setStatus(s);
    // So refaz se ja ha relatorio na tela — antes disso nao ha o que recortar.
    if (resultado) gerar.mutate({ pagina: 1, porPagina, status: s });
  });

  // Os arquivos levam o CPF completo — a averbadora ve tudo (diretriz de
  // 14/10/2026). A mascara e' so' da tela.
  async function baixar(format: "csv" | "xlsx") {
    if (!bancoId) return;
    const res = await fetch(`${apiBaseUrl}/v1/admin/bate-carteira`, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("atlas:tokens") ? JSON.parse(localStorage.getItem("atlas:tokens")!).access_token : ""}`,
      },
      body: JSON.stringify({ bancoId, competencia, unidadeId: unidadeId === "all" ? undefined : unidadeId, format }),
    });
    if (!res.ok) {
      alert(`Erro ${res.status}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bate-carteira-${bancoId}-${competencia}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Bate-de-carteira</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 720 }}>
          Relatório mensal por consignatária para conciliação: um contrato por linha — o contrato ativo na competência ou com
          retorno da folha — e o que a folha fez com ele. Em Excel ou CSV. Só o status <strong>Descontado</strong> quer dizer que a
          parcela saiu inteira.
        </p>
      </header>

      <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 20 }}>
        <FormGrid cols={3}>
          <SelectField
            label="Consignatária"
            value={bancoId ? String(bancoId) : ""}
            onChange={(e) => setBancoId(e.target.value ? Number(e.target.value) : null)}
            options={[{ value: "", label: "—" }, ...(bancos.data?.bancos.map((b) => ({ value: String(b.id), label: rotuloCredor(b) })) ?? [])]}
          />
          <TextField
            label="Competência (YYYYMM)"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
            maxLength={6}
          />
          <SelectField
            label="Órgão/entidade (opcional)"
            value={String(unidadeId)}
            onChange={(e) => setPrefeituraId(e.target.value === "all" ? "all" : Number(e.target.value))}
            options={[{ value: "all", label: "Todas" }, ...(prefeituras.data?.prefeituras.map((p) => ({ value: String(p.id), label: rotuloUnidade(p) })) ?? [])]}
          />
        </FormGrid>
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <Button onClick={() => { setPagina(1); gerar.mutate({ pagina: 1, porPagina, status }); }} disabled={!bancoId || gerar.isPending}>
            {gerar.isPending ? "Gerando..." : "Gerar relatório"}
          </Button>
          <Button variant="ghost" onClick={() => void baixar("xlsx")} disabled={!bancoId}>Baixar Excel</Button>
          <Button variant="ghost" onClick={() => void baixar("csv")} disabled={!bancoId}>Baixar CSV</Button>
        </div>
      </div>

      {resultado ? (
        <>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            <b style={{ color: "var(--text)" }}>{resultado.bancoNome}</b> · competência {resultado.competencia}
          </div>
          <CardsPorStatus resultado={resultado} statusAtivo={status} onStatus={trocarStatus} />
          <AtualidadeDoRelatorio atualidade={resultado.atualidade} />
          {/* Paginar sem filtrar so troca rolagem por virar pagina (G4). */}
          <div style={GRADE_FILTROS}>
            <FilterChips
              label="Status"
              value={status ?? ""}
              onChange={(v) => trocarStatus(v ? (v as StatusNaFolha) : null)}
              options={[{ value: "", label: "Todos" }, ...STATUS_NA_FOLHA.map((s) => ({ value: s, label: ROTULO_STATUS_NA_FOLHA[s] }))]}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-muted)" }}>
            <OlhoBotao rotulo="CPFs" revelado={cpfsRevelados} onAlternar={() => setCpfsRevelados((v) => !v)} />
            <span>{cpfsRevelados ? "CPFs visíveis" : "CPFs mascarados"}</span>
          </div>
          <DataTable
            columns={colunasDaCarteira(cpfsRevelados)}
            rows={resultado.linhas}
            rowKey={(l) => l.adfBanco}
            emptyState={status ? "Nenhum contrato com este status." : "Nenhum contrato na carteira nesta competência."}
          />
          <Pagination
            total={recorte.total}
            pagina={recorte.pagina}
            porPagina={porPagina}
            onPagina={irPara}
            onPorPagina={trocarPorPagina}
            rotulo="contratos"
          />
          <ForaDaCarteira itens={resultado.foraDaCarteira} cpfsRevelados={cpfsRevelados} />
        </>
      ) : (
        <div style={{ padding: 32, textAlign: "center", color: "var(--text-dim)" }}>
          Selecione consignatária e competência para gerar o relatório.
        </div>
      )}
    </div>
  );
}
